import { cva } from 'class-variance-authority';

/** Outer calendar container — border/shadow/rounded only on desktop (mobile is inside the bottom sheet dialog).
 *  Desktop height is set inline (capped to the available viewport space, up to the Figma 395px frame)
 *  and uses a flex column so the day grid fills the remaining space while the header / weekday row /
 *  footer stay fixed and the grid scrolls if the frame is shorter than its content. */
export const calendarContainerClasses =
  'tw:bg-white tw:overflow-x-hidden tw:overflow-y-auto tw:select-none tw:md:flex tw:md:flex-col tw:md:rounded-[var(--radius-16)] tw:md:border tw:md:border-solid tw:md:border-gray-light tw:md:shadow-lg';

/** Outer container when time panel is enabled — same desktop-only decoration. */
export const calendarWithTimeContainerClasses =
  'tw:bg-white tw:overflow-x-hidden tw:overflow-y-auto tw:select-none tw:md:flex tw:md:flex-col tw:md:rounded-[var(--radius-16)] tw:md:border tw:md:border-solid tw:md:border-gray-light tw:md:shadow-lg';

/** Wrapper for calendar body + time panel — a flex row so both columns sit in flow and
 *  the dropdown measures its full width (calendar + time). Fills the remaining height
 *  below the footer; min-h-0 lets the calendar grid and time list shrink/scroll. */
export const calendarWithTimeBodyClasses = 'tw:flex tw:flex-1 tw:min-h-0';

/** Header row containing month/year selectors. */
export const calendarHeaderClasses =
  'tw:flex tw:shrink-0 tw:items-center tw:justify-between tw:gap-[var(--spacing-small)] tw:px-[var(--spacing-base)] tw:pt-[var(--spacing-base)] tw:pb-[var(--spacing-small)]';

/** Month/year selector button style (replacing <select> with toggle buttons). */
export const calendarSelectorButtonClasses = cva(
  [
    'tw:inline-flex tw:items-center tw:gap-[var(--spacing-nano)]',
    'tw:rounded-[var(--radius-8)] tw:border tw:border-solid',
    'tw:px-[var(--spacing-small)] tw:py-[var(--spacing-micro)]',
    'tw:text-14 tw:font-semibold tw:shadow-sm',
    'tw:cursor-pointer tw:transition-colors',
    'tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-primary',
  ].join(' '),
  {
    variants: {
      active: {
        true: 'tw:bg-primary tw:text-white tw:border-primary tw:hover:bg-primary-dark',
        false: 'tw:bg-white tw:text-font tw:border-gray tw:hover:bg-gray-lighter',
      },
    },
    defaultVariants: {
      active: false,
    },
  }
);

/** Day-of-week header row (SUN–SAT). */
export const calendarWeekHeaderClasses =
  'tw:grid tw:shrink-0 tw:grid-cols-7 tw:gap-[var(--spacing-micro)] tw:px-[var(--spacing-base)] tw:pt-[var(--spacing-small)] tw:pb-0';

/** Individual day-of-week header cell. */
export const calendarWeekHeaderCellClasses =
  'tw:flex tw:items-center tw:justify-center tw:py-[var(--spacing-micro)] tw:text-12 tw:font-regular tw:text-font tw:bg-gray-lighter tw:rounded-[var(--radius-8)] tw:border tw:border-solid tw:border-gray-light tw:no-underline';

/** Day grid (7 columns). On desktop the grid fills the remaining height inside the
 *  fixed-height container so cells shrink to fit (5- or 6-week months) instead of
 *  overflowing past the 395px frame. */
export const calendarGridClasses =
  'tw:grid tw:grid-cols-7 tw:gap-[var(--spacing-micro)] tw:px-[var(--spacing-base)] tw:py-[var(--spacing-small)] tw:md:flex-1 tw:md:min-h-0 tw:md:auto-rows-fr';

/** Day cell button (CVA with state variants). */
export const calendarDayCellClasses = cva(
  [
    'tw:flex tw:items-center tw:justify-center',
    'tw:w-full tw:aspect-square tw:md:aspect-auto tw:md:h-full',
    'tw:rounded-[var(--radius-8)]',
    'tw:text-14 tw:font-regular',
    'tw:transition-colors tw:duration-150',
    'tw:p-0',
    'tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-primary tw:focus-visible:ring-inset',
  ].join(' '),
  {
    variants: {
      state: {
        default:
          'tw:text-font tw:bg-white tw:cursor-pointer tw:hover:bg-gray-lighter tw:border tw:border-solid tw:border-gray-lighter',
        today:
          'tw:text-accent tw:font-semibold tw:bg-white tw:cursor-pointer tw:hover:bg-gray-lighter tw:border tw:border-solid tw:border-gray-lighter',
        selected:
          'tw:bg-primary tw:text-white tw:font-semibold tw:cursor-pointer tw:border tw:border-solid tw:border-primary',
        disabled:
          'tw:text-gray tw:opacity-50 tw:cursor-not-allowed tw:border tw:border-solid tw:border-transparent',
        adjacent:
          'tw:text-gray-dark tw:bg-white tw:cursor-pointer tw:hover:bg-gray-lighter tw:border tw:border-solid tw:border-gray-lighter',
      },
    },
    defaultVariants: {
      state: 'default',
    },
  }
);

/** Footer row with prev/next navigation and Done button. */
export const calendarFooterClasses =
  'tw:flex tw:shrink-0 tw:items-center tw:border-t tw:border-solid tw:border-gray-light tw:px-[var(--spacing-base)] tw:py-[var(--spacing-small)]';

/** Left-grouped navigation button wrapper in the footer. */
export const calendarFooterNavGroupClasses =
  'tw:flex tw:items-center tw:gap-[var(--spacing-small)]';

/** Spacer pushes Done button to the right. */
export const calendarFooterSpacerClasses = 'tw:flex-1';

/** Prev/Next navigation button. */
export const calendarNavButtonClasses =
  'tw:inline-flex tw:items-center tw:justify-center tw:rounded-[var(--radius-8)] tw:border tw:border-solid tw:border-gray tw:bg-white tw:p-[var(--spacing-micro)] tw:shadow-sm tw:cursor-pointer tw:transition-colors tw:hover:bg-gray-lighter tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-primary tw:text-font';

/** Done (confirm) button. */
export const calendarDoneButtonClasses =
  'tw:inline-flex tw:items-center tw:justify-center tw:rounded-[var(--radius-8)] tw:bg-primary tw:p-[var(--spacing-small)] tw:shadow-sm tw:cursor-pointer tw:transition-colors tw:hover:bg-primary-dark tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-primary tw:text-white';

/** Invisible overlay behind the desktop dropdown to establish stacking context. */
export const calendarDesktopOverlayClasses = 'tw:fixed tw:inset-0 tw:z-40 tw:pointer-events-none';

/** Desktop dropdown container (fixed positioning). */
export const calendarDesktopDropdownClasses = 'tw:z-50 tw:fixed';

/** Dimmed backdrop behind the mobile bottom sheet. */
export const calendarMobileBackdropClasses =
  'tw:fixed tw:inset-0 tw:z-40 tw:bg-black/40 tw:transition-opacity animate-backdrop-fade-in';

/** Mobile bottom sheet dialog container. */
export const calendarMobileDialogClasses =
  'tw:fixed tw:bottom-0 tw:left-0 tw:right-0 tw:z-50 tw:flex tw:max-h-[85vh] tw:flex-col tw:rounded-t-[var(--radius-16)] tw:border tw:border-solid tw:border-gray tw:border-b-0 tw:bg-white tw:shadow-lg tw:transition-transform animate-bottomsheet-slide-up';

/** Header row in the mobile dialog. */
export const calendarMobileHeaderClasses =
  'tw:flex tw:shrink-0 tw:items-center tw:justify-between tw:gap-[var(--spacing-nano)] tw:border-b tw:border-solid tw:border-gray tw:px-[var(--spacing-base)] tw:py-[var(--spacing-small)]';

/** Title text in the mobile dialog header. */
export const calendarMobileTitleClasses = 'tw:text-16 tw:font-bold tw:text-font';

/** Close button in the mobile dialog header. */
export const calendarMobileCloseButtonClasses =
  'tw:flex tw:items-center tw:justify-center tw:rounded tw:p-1 tw:text-gray-dark tw:transition-opacity tw:hover:opacity-80 tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-primary';

/** Scrollable body wrapper in the mobile dialog. */
export const calendarMobileBodyClasses =
  'tw:min-h-0 tw:flex-1 tw:overflow-y-auto';
